import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { DbLead, LeadStatus } from "@/lib/types";

/** Standalone update + audit-log call, usable without pulling in the full useLeads() list-fetch. */
export async function updateLeadRecord(userId: string | undefined, id: string, updates: Partial<DbLead>) {
  const { error } = await supabase.from("leads").update(updates).eq("id", id);
  if (error) throw error;
  if (userId) {
    (supabase as any).rpc("log_audit_event", {
      _action: "lead_updated",
      _resource_type: "lead",
      _resource_id: id,
      _metadata: { fields: Object.keys(updates) },
    }).then(() => {}, () => {});
  }
}

export function useLeads(options?: { assignedToMe?: boolean }) {
  const { user } = useAuth();
  const assignedToMe = options?.assignedToMe ?? false;
  const [leads, setLeads] = useState<DbLead[]>([]);
  const [loading, setLoading] = useState(true);

  // Cap fetched rows. Most users have <500 active leads; cold/closed leads
  // beyond this can be loaded on demand from the dedicated Leads page.
  // Prevents the polling loop from ever pulling thousands of rows.
  //
  // When scoped to assignedToMe, the cap applies AFTER the assigned_to
  // filter (server-side), not before — otherwise a staffer's older assigned
  // leads could fall outside the agency's newest 500 and silently vanish
  // from their task list. Their own workload is bounded regardless.
  const PAGE_LIMIT = 500;

  const fetchLeads = useCallback(async () => {
    if (!user) { setLeads([]); setLoading(false); return; }
    let query = supabase
      .from("leads")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(PAGE_LIMIT);
    if (assignedToMe) query = query.eq("assigned_to", user.id);
    const { data } = await query;
    setLeads(data ?? []);
    setLoading(false);
  }, [user, assignedToMe]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Poll for updates — global postgres_changes subscription was removed because
  // it streamed PHI cross-tenant. Lead-table updates now use 20s polling per user.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchLeads, 20000);
    return () => clearInterval(interval);
  }, [user, fetchLeads]);

  const addLead = useCallback(async (lead: Omit<DbLead, "id" | "created_at" | "updated_at">) => {
    const { data, error } = await supabase.from("leads").insert(lead).select("id").single();
    if (error) throw error;
    if (user && data?.id) {
      (supabase as any).rpc("log_audit_event", {
        _action: "lead_created",
        _resource_type: "lead",
        _resource_id: data.id,
        _metadata: { name: lead.name },
      }).then(() => {}, () => {});
    }
  }, [user]);

  const updateLead = useCallback(async (id: string, updates: Partial<DbLead>) => {
    await updateLeadRecord(user?.id, id, updates);
  }, [user]);

  const getByStatus = useCallback((status: LeadStatus) => leads.filter(l => l.status === status), [leads]);

  return { leads, loading, addLead, updateLead, getByStatus, refetch: fetchLeads };
}
